import {
    LOW_DEMAND_MODE,
    HIGH_DEMAND_MODE,
    CONTINUOUS_MODE,
    PROVEN_NO,
    PROVEN_IN_USE,
    PRIOR_USE,
} from "const"
import {
    SilResultProjectDevice,
    MtDriveVersion,
} from "server/models"
import { IMAGE_BASE64_TYPE, DEVICE_REFERENCE_TYPE } from "client/utils"
import {
    LangType,
    UserSettings,
} from "client/store"

import { getSilMode } from "client/pages/sil/formulas"
import { FormatProbabilityString } from "client/pages/sil/format/probability"
import { FormatScientificString } from "client/pages/sil/format/scientific"
import { FormatPercentageString } from "client/pages/sil/format/percentage"
import * as Formulas from "client/pages/sil/formulas"

import * as Const from "client/components/pdfmake/template/const"

const AppendixAPage = (
    deviceArrayOrdered: SilResultProjectDevice[],
    deviceImageBase64: IMAGE_BASE64_TYPE,
    deviceReferenceList: DEVICE_REFERENCE_TYPE,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const content: any[] = [
        {
            pageBreak: "before",
            style: Const.HEADER,
            text: `3.  ${lang.appendix} A - ${lang.projectDevices}`,
            ...Const.TOC_HEADER_BASE,
        },
    ]

    deviceArrayOrdered.forEach((device, index) => {
        const list: string[] = [
            `${lang.manufacturer}: ${device.manufacturer}`,
        ]
        if (device.qidModelId) list.push(`${lang.model}: ${device.qidModelName}`)
        if (device.qidSafetyFunctionId) list.push(`${lang.safetyFunction}: ${device.qidSafetyFunctionName}`)
        if (device.qidOptionId) list.push(`${lang.option}: ${device.qidOptionName}`)
        if (device.qidDatapointId) list.push(`${lang.datapoint}: ${device.qidDatapointDate}`)
        if (device.qidMethod) list.push(`${lang.method}: ${lang[device.qidMethod]}`)

        const mode = getSilMode(device)
        let modeTitle: string
        if (mode === LOW_DEMAND_MODE) modeTitle = lang.modeLowDemand
        if (mode === HIGH_DEMAND_MODE) modeTitle = lang.modeHighDemand
        if (mode === CONTINUOUS_MODE) modeTitle = lang.modeContinuous

        let proven: string
        if (device.provenInUse === PROVEN_NO) proven = lang.no
        if (device.provenInUse === PROVEN_IN_USE) proven = lang.provenInUse
        if (device.provenInUse === PRIOR_USE) proven = lang.priorUse

        list.push(
            `${lang.type}: ${device.type}`,
            `${lang.mode}: ${modeTitle}`,
            `${lang.provenReliabilityData}: ${proven}`,
            `${lang.logicSolver}: ${device.logicSolver ? lang.yes : lang.no}`,
            `${lang.failureRateSD}: ${FormatScientificString(device.sd, userSettings, lang)}`,
            `${lang.failureRateSU}: ${FormatScientificString(device.su, userSettings, lang)}`,
            `${lang.failureRateDD}: ${FormatScientificString(device.dd, userSettings, lang)}`,
            `${lang.failureRateDU}: ${FormatScientificString(device.du, userSettings, lang)}`,
        )

        if (device.failureRate) {
            const sffCalculated = Formulas.sff({
                sd: device.sd,
                su: device.su,
                dd: device.dd,
                du: device.du,
            })
            const dcCalculated = Formulas.dc({
                dd: device.dd,
                du: device.du,
            })

            list.push(
                `${lang.sffCalculated}: ${
                    sffCalculated === false
                        ? lang.insufficientData
                        : FormatPercentageString(sffCalculated, userSettings, lang)
                }`,
                `${lang.dcCalculated}: ${
                    dcCalculated === false
                        ? lang.insufficientData
                        : FormatPercentageString(dcCalculated, userSettings, lang)
                }`,
            )
        } else {
            list.push(
                `${lang.sffGiven}: ${FormatPercentageString(device.sff, userSettings, lang)}`,
                `${lang.dcGiven}: ${FormatPercentageString(device.dc, userSettings, lang)}`,
            )
        }
        if (device.pfd !== null) {
            list.push(
                `${lang.pfd}: ${FormatProbabilityString(device.pfd, userSettings, lang)}`,
            )
        }
        if (device.pfh !== null) {
            list.push(
                `${lang.pfh}: ${FormatProbabilityString(device.pfh, userSettings, lang)}`,
            )
        }
        if (device.sil) {
            list.push(
                lang.givenSil.replace("{0}", device.sil.toString()),
            )
        }

        // reference
        const reference = deviceReferenceList[device.id]
        const referenceVersion = reference as MtDriveVersion
        const referenceString = reference as String
        let referenceTitle: string = null

        if (referenceVersion && referenceVersion.id) {
            const referenceTitleArray: string[] = []

            const name = referenceVersion.name.trim()
            const reportNumber = referenceVersion.reportNumber.trim()
            const { publicationDate } = referenceVersion

            if (name && name.length) referenceTitleArray.push(name)
            if (reportNumber && reportNumber.length) referenceTitleArray.push(reportNumber)
            if (publicationDate) referenceTitleArray.push(publicationDate.toString())

            referenceTitle = referenceTitleArray.join(", ")
        } else if (referenceString && referenceString.trim().length) {
            referenceTitle = referenceString.trim()
        }

        list.push(`${lang.reference}: ${referenceTitle || lang.toBeAdded}`)


        content.push(
            {
                style: Const.SUB_HEADER,
                text: `A${index + 1} - ${lang.device} ${index + 1} - ${device.name}`,
                ...Const.TOC_ITEM_BASE,
            },
            {
                fontSize: 11,
                columns: [
                    {
                        width: "*",
                        style: Const.LIST,
                        ul: list,
                        margin: [30, 0, 0, 0],
                    },
                    {
                        width: "25%",
                        stack: [{
                            image: deviceImageBase64[device.id].data,
                            fit: [90, 90],
                            alignment: "right",
                        }],
                    },
                ],
            },
        )
    })

    return content
}
export default AppendixAPage
